import { RowActionMenuItem } from '@approvalmax/ui/src/components';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    addQbooksAccountLineItem,
    changeQbooksAccountLineItemChecked,
    cloneQbooksAccountLineItems,
    removeQbooksAccountLineItems,
    reorderQbooksAccountLineItem,
} from '../../../../actions';
import { ExpandedQBooksAccountLineItem } from '../../../../selectors/types/ExpandedQBooksAccountLineItem';
import { messages } from '../QBooksAccountLineItemsSection.messages';
import type { Line } from '../QBooksAccountLineItemsSection.types';
import { useAccountLineItems } from './useAccountLineItems';
import { useIsApproverMode } from './useIsApproverMode';

export const useTableLineActions = () => {
    const dispatch = useDispatch();

    return useMemo<RowActionMenuItem<Line>[]>(
        () => [
            { label: messages.actionCloneLine, onClick: (item) => dispatch(cloneQbooksAccountLineItems([item.id])) },
            { label: messages.actionRemoveLine, onClick: (item) => dispatch(removeQbooksAccountLineItems([item.id])) },
        ],
        [dispatch]
    );
};

export const useCheckedLineItems = (accountLineItems: ExpandedQBooksAccountLineItem[]) => {
    return useMemo(() => {
        return accountLineItems.reduce<string[]>((total, lineItem) => {
            if (lineItem.checked) {
                total.push(lineItem.id);
            }

            return total;
        }, []);
    }, [accountLineItems]);
};

export const useTableLinesCheckboxes = () => {
    const dispatch = useDispatch();
    const accountLineItems = useAccountLineItems();

    const withLineCheckboxes = !useIsApproverMode();
    const checkedItemsIds = useCheckedLineItems(accountLineItems);

    const handleCheckItem = useCallback(
        (lineItemIds: string[]) => {
            accountLineItems.forEach((lineItem) => {
                const isChecked = lineItemIds.includes(lineItem.id);

                dispatch(changeQbooksAccountLineItemChecked(lineItem.id, isChecked));
            });
        },
        [accountLineItems, dispatch]
    );

    return {
        checkedItems: withLineCheckboxes ? checkedItemsIds : undefined,
        onCheckedItemsChange: withLineCheckboxes ? handleCheckItem : undefined,
    };
};

export const useTableLinesManaging = () => {
    const dispatch = useDispatch();

    const handleAddLine = useCallback(() => dispatch(addQbooksAccountLineItem()), [dispatch]);

    const handleCloneLine = useCallback(
        (lineId: string) => dispatch(cloneQbooksAccountLineItems([lineId])),
        [dispatch]
    );

    const handleRemoveLine = useCallback(
        (lineId: string) => dispatch(removeQbooksAccountLineItems([lineId])),
        [dispatch]
    );

    return {
        handleAddLine,
        handleCloneLine,
        handleRemoveLine,
    };
};

export const useTableLinesReordering = () => {
    const dispatch = useDispatch();

    const handleReorderLines = useCallback(
        (_rows: unknown, oldIndex: number, newIndex: number) =>
            dispatch(reorderQbooksAccountLineItem(oldIndex, newIndex)),
        [dispatch]
    );

    return {
        handleReorderLines,
    };
};
